import React from 'react';
import { useState } from 'react'
const MatchMakerSettings = ({
    colsCount,
    setColsCount,
    autoRotation,
    setAutoRotation,
    longWaitThreshold,
    setLongWaitThreshold,
    lookForPlayers,
    setLookForPlayers,
    maxLevelDiff,
    setMaxLevelDiff,
    showAutoMatch,
    setShowAutoMatch,
    THEME_COLOR,
    courts,
    handleCourtChange,
    limitTeammatesEnabled,
    setLimitTeammatesEnabled,
    limitRivalsEnabled,
    setLimitRivalsEnabled,
    maxTeammates,
    setMaxTeammates,
    maxRivals,
    setMaxRivals,
    maxTeamDiff,
    setMaxTeamDiff,
}) => {
    const [showAutoInfo, setShowAutoInfo] = useState(false);
    return (
        <div className="p-1">
            {/* 1. Display Preferences */}
            <section className="mb-4">
                <label className="fw-black text-uppercase small text-muted mb-3 italic" style={{ letterSpacing: '1px' }}>
                    <i className="bi bi-display me-2"></i>Display Preferences
                </label>

                <div className="card border-0 rounded-4 p-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fw-bold mb-0">Grid Density</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>How many players to show per row</div>
                        </div>

                        <div className="btn-group p-1 rounded-pill border shadow-sm" role="group">
                            {[3, 4, 5, 6].map((num) => (
                                <button
                                    key={num}
                                    className={`btn btn-sm rounded-pill px-3 py-1 border-0 fw-bold ${colsCount === num ? 'bg-primary text-white shadow-sm' : 'text-muted'}`}
                                    style={{ backgroundColor: colsCount === num ? THEME_COLOR : 'transparent', fontSize: '0.75rem' }}
                                    onClick={() => setColsCount(num)}
                                >
                                    {num} Columns
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. Match Logic Settings */}
            <section className="mb-4">
                <label className="fw-black text-uppercase small text-muted mb-3 italic" style={{ letterSpacing: '1px' }}>
                    <i className="bi bi-gear-wide-connected me-2"></i>Rotation Logic
                </label>

                <div className="card border-0 rounded-4 p-3 gap-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fw-bold mb-0">Court Count</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>Number of usable courts</div>
                        </div>

                        <div className="d-flex align-items-center bg-opacity-10 rounded-pill p-1">
                            {/* Minus Button */}
                            <button
                                className="btn btn-link p-0 px-2 text-decoration-none border-0 shadow-none"
                                onClick={() => handleCourtChange(Math.max(1, courts - 1))}
                            >
                                <i className="bi bi-dash-lg fw-bold"></i>
                            </button>
                            <div className="vr opacity-25" style={{ height: '15px' }}></div>
                            {/* THE COURT NUMBER DISPLAY */}
                            <div
                                className="fw-black px-2"
                                style={{ minWidth: '30px', textAlign: 'center', fontSize: '0.9rem' }}
                            >
                                {courts}
                            </div>

                            <div className="vr opacity-25" style={{ height: '15px' }}></div>

                            {/* Plus Button */}
                            <button
                                className="btn btn-link p-0 px-2 text-decoration-none border-0 shadow-none"
                                onClick={() => handleCourtChange(courts + 1)}
                            >
                                <i className="bi bi-plus-lg fw-bold"></i>
                            </button>
                        </div>
                    </div>

                    <hr className="my-1 opacity-10" />

                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fw-bold mb-0">Smart Matchmaker</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>Show smart matchmaker button</div>
                        </div>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input cursor-pointer"
                                type="checkbox"
                                role="switch"
                                checked={showAutoMatch}
                                onChange={(e) => setShowAutoMatch(e.target.checked)}
                                style={{ width: '2.5em', height: '1.25em' }}
                            />
                        </div>

                    </div>
                    <div className="mt-2">

                        {/* Toggle Button */}
                        <button
                            className="btn btn-link p-0 text-decoration-none small fw-bold"
                            type="button"
                            onClick={() => setShowAutoInfo(!showAutoInfo)}
                        >
                            <i className={`bi ${showAutoInfo ? "bi-chevron-down" : "bi-info-circle"} me-1`}></i>
                            How Smart Matchmaker Works
                        </button>

                        {/* Collapsible Content */}
                        {showAutoInfo && (
                            <div
                                className="rounded-3 p-2 mt-2 small text-muted"
                                style={{
                                    fontSize: "0.72rem",
                                    background: "color-mix(in srgb, var(--card-bg) 85%, black)"
                                }}
                            >
                                <ul className="mb-0 ps-3">
                                    <li>Prioritizes the longest waiting player.</li>
                                    <li>Scans the first <b>{lookForPlayers}</b> players in the queue.</li>
                                    <li>Keeps teammates within <b>{maxLevelDiff}</b> level difference.</li>
                                    <li>Ensures team averages differ by at most <b>{maxTeamDiff}</b>.</li>
                                    <li>Avoids repeated teammates and rivals using match history.</li>

                                    {limitTeammatesEnabled && (
                                        <li>Teammates can only repeat up to <b>{maxTeammates}</b> times.</li>
                                    )}

                                    {limitRivalsEnabled && (
                                        <li>Players can face the same opponent up to <b>{maxRivals}</b> times.</li>
                                    )}

                                    <li>Players waiting longer receive priority in match selection.</li>
                                </ul>
                            </div>
                        )}

                    </div>
                    <hr className="my-1 opacity-10" />

                    {/* New Inputs: Side by Side */}
                    <div className="row g-2">
                        <div className="col-6">
                            <label className="form-label small fw-bold opacity-75 mb-1" style={{ fontSize: '0.7rem' }}>
                                Queue Depth:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text border-end-0 border-0 shadow-sm rounded-start-3">
                                    <i className="bi bi-people text-muted"></i>
                                </span>
                                <input
                                    type="number"
                                    min={6}
                                    className="form-control border-0 shadow-sm rounded-end-3 fw-bold"
                                    value={lookForPlayers}
                                    onChange={(e) => setLookForPlayers(parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                Number of players the matchmaker scans
                            </div>
                        </div>

                        <div className="col-6">
                            <label className="form-label small fw-bold opacity-75 mb-1" style={{ fontSize: '0.7rem' }}>
                                Max Lvl Diff:
                            </label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text border-end-0 border-0 shadow-sm rounded-start-3">
                                    <i className="bi bi-diagram-3 text-muted"></i>
                                </span>
                                <input
                                    type="number"
                                    min={0}
                                    className="form-control border-0 shadow-sm rounded-end-3 fw-bold"
                                    value={maxLevelDiff}
                                    onChange={(e) => setMaxLevelDiff(parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                Max difference between player levels
                            </div>
                        </div>
                    </div>

                    <hr className="my-1 opacity-10" />

                    {/* Team Balance Control */}
                    <div className="col-12">
                        <label className="form-label small fw-bold opacity-75 mb-1" style={{ fontSize: '0.7rem' }}>
                            Max Team Diff:
                        </label>

                        <div className="input-group input-group-sm">
                            <span className="input-group-text border-end-0 border-0 shadow-sm rounded-start-3">
                                <i className="bi bi-bar-chart text-muted"></i>
                            </span>

                            <input
                                type="number"
                                min={0}
                                className="form-control border-0 shadow-sm rounded-end-3 fw-bold"
                                value={maxTeamDiff}
                                onChange={(e) => setMaxTeamDiff(parseInt(e.target.value) || 0)}
                            />
                        </div>

                        <div className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                            Max difference between team level avg
                        </div>
                    </div>

                    <hr className="my-1 opacity-10" />

                    {/* Match Variety Controls */}
                    <div className="row g-3">

                        {/* Teammate Limit */}
                        <div className="col-6">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="form-label small fw-bold opacity-75 mb-0" style={{ fontSize: '0.7rem' }}>
                                    Teammate Limit
                                </label>

                                <div className="form-check form-switch m-0">
                                    <input
                                        className="form-check-input cursor-pointer"
                                        type="checkbox"
                                        checked={limitTeammatesEnabled}
                                        onChange={(e) => setLimitTeammatesEnabled(e.target.checked)}
                                        style={{ width: '2em', height: '1em' }}
                                    />
                                </div>
                            </div>

                            <div className="input-group input-group-sm">
                                <span className="input-group-text border-0 shadow-sm rounded-start-3">
                                    <i className="bi bi-person-check text-muted"></i>
                                </span>

                                <input
                                    type="number"
                                    min={0}
                                    className="form-control border-0 shadow-sm rounded-end-3 fw-bold"
                                    disabled={!limitTeammatesEnabled}
                                    value={maxTeammates}
                                    onChange={(e) => setMaxTeammates(parseInt(e.target.value) || 0)}
                                />
                            </div>

                            <div className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                Max times players can team together
                            </div>
                        </div>


                        {/* Rival Limit */}
                        <div className="col-6">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="form-label small fw-bold opacity-75 mb-0" style={{ fontSize: '0.7rem' }}>
                                    Rival Limit
                                </label>

                                <div className="form-check form-switch m-0">
                                    <input
                                        className="form-check-input cursor-pointer"
                                        type="checkbox"
                                        checked={limitRivalsEnabled}
                                        onChange={(e) => setLimitRivalsEnabled(e.target.checked)}
                                        style={{ width: '2em', height: '1em' }}
                                    />
                                </div>
                            </div>

                            <div className="input-group input-group-sm">
                                <span className="input-group-text border-0 shadow-sm rounded-start-3">
                                    <i className="bi bi-lightning-charge text-muted"></i>
                                </span>

                                <input
                                    type="number"
                                    min={0}
                                    className="form-control border-0 shadow-sm rounded-end-3 fw-bold"
                                    disabled={!limitRivalsEnabled}
                                    value={maxRivals}
                                    onChange={(e) => setMaxRivals(parseInt(e.target.value) || 0)}
                                />
                            </div>

                            <div className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                Max times players can face each other
                            </div>
                        </div>

                    </div>
                    <hr className="my-0 opacity-10" />
                    {/* Long Wait Threshold */}
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fw-bold mb-0">Long Wait Alert</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>Highlight players waiting over (mins)</div>
                        </div>
                        <div style={{ width: '80px' }}>
                            <select
                                className="form-select form-select-sm rounded-3 fw-bold border-0 shadow-sm"
                                value={longWaitThreshold}
                                onChange={(e) => setLongWaitThreshold(Number(e.target.value))}
                            >
                                <option value="15">15m</option>
                                <option value="20">20m</option>
                                <option value="25">25m</option>
                                <option value="30">30m</option>
                                <option value="35">35m</option>
                                <option value="40">40m</option>
                                <option value="45">45m</option>
                            </select>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. System Info / Danger Zone */}
            {/* <section>
        <label className="fw-black text-uppercase small text-muted mb-3 italic" style={{ letterSpacing: '1px' }}>
          <i className="bi bi-shield-lock me-2"></i>Session Control
        </label>
        <button 
          className="btn btn-outline-danger w-100 rounded-4 fw-bold py-2 border-dashed"
          onClick={() => }
        >
          <i className="bi bi-arrow-counterclockwise me-2"></i>Reset Entire Session
        </button>
      </section> */}
        </div>
    );
};

export default MatchMakerSettings;